import { ChangeDetectionStrategy, Component, TemplateRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { merge, startWith } from 'rxjs';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { TableAction, TableActionsComponent } from '@components/table-actions/table-actions.component';
import { ExternalSceneRowViewModel } from '../../external-scenes/external-scenes.models';
import { DOWNLOAD_SPEED_MBIT_PER_SECOND } from '../../external-scenes/external-scenes-format.util';
import { ExternalSceneTableRow, resolveActionState, toTableRow } from '../../external-scenes/external-scenes-view.util';

/** Table row plus its precomputed row actions, so the template calls no methods. */
interface ExternalSceneActionRow extends ExternalSceneTableRow {
  actions: TableAction[];
}

const PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Result table of the `download` tab: Quick View thumbnail, scene, source, date and cloud cover, archive size,
 * estimated download time, status and the Load action. Purely presentational: it renders the row view models
 * of the store and reports clicks.
 */
@Component({
  selector: 'app-external-scenes-table',
  standalone: true,
  templateUrl: './external-scenes-table.component.html',
  styleUrl: './external-scenes-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe, DataTableComponent, TableActionsComponent]
})
export class ExternalScenesTableComponent {
  readonly rows = input<readonly ExternalSceneRowViewModel[]>([]);

  /** Emits the row id when its thumbnail is pressed. */
  readonly previewClick = output<string>();
  /** Emits the row id when its enabled Load button is pressed. */
  readonly loadClick = output<string>();

  readonly downloadSpeed = DOWNLOAD_SPEED_MBIT_PER_SECOND;

  readonly config: TableConfig = {
    paginator: true,
    rows: PAGE_SIZE,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    keepOrder: true,
    emptyMessage: 'EXTERNAL_SCENES.EMPTY_STATE'
  };

  private readonly failedPreviewIds = signal<ReadonlySet<string>>(new Set());

  private readonly translate = inject(TranslateService);
  /** Ticks whenever the active language or its translations change, so row action labels are re-translated. */
  private readonly translationVersion = toSignal(
    merge(this.translate.onLangChange, this.translate.onTranslationChange, this.translate.onDefaultLangChange).pipe(
      startWith(null)
    )
  );

  readonly tableRows = computed<ExternalSceneActionRow[]>(() => {
    this.translationVersion();
    return this.rows().map(row => ({
      ...toTableRow(row, this.failedPreviewIds()),
      actions: [this.toRowAction(row)]
    }));
  });

  private readonly quickViewCell = viewChild.required<TemplateRef<unknown>>('quickViewCell');
  private readonly sceneCell = viewChild.required<TemplateRef<unknown>>('sceneCell');
  private readonly sourceCell = viewChild.required<TemplateRef<unknown>>('sourceCell');
  private readonly dateCloudCell = viewChild.required<TemplateRef<unknown>>('dateCloudCell');
  private readonly sizeCell = viewChild.required<TemplateRef<unknown>>('sizeCell');
  private readonly etaCell = viewChild.required<TemplateRef<unknown>>('etaCell');
  private readonly statusCell = viewChild.required<TemplateRef<unknown>>('statusCell');
  private readonly actionCell = viewChild.required<TemplateRef<unknown>>('actionCell');

  readonly columns = computed<TableColumn[]>(() => [
    { field: 'previewUrl', header: 'EXTERNAL_SCENES.COLUMN_QUICK_VIEW', type: 'custom', customTemplate: this.quickViewCell(), width: '130px' },
    { field: 'name', header: 'EXTERNAL_SCENES.COLUMN_NAME', type: 'custom', customTemplate: this.sceneCell(), minWidth: '220px' },
    { field: 'provider', header: 'EXTERNAL_SCENES.COLUMN_SOURCE', type: 'custom', customTemplate: this.sourceCell(), width: '120px' },
    { field: 'acquiredAt', header: 'EXTERNAL_SCENES.COLUMN_DATE_CLOUD', type: 'custom', customTemplate: this.dateCloudCell(), width: '140px' },
    { field: 'sizeLabel', header: 'EXTERNAL_SCENES.COLUMN_SIZE', type: 'custom', customTemplate: this.sizeCell(), width: '110px' },
    { field: 'etaMinutes', header: 'EXTERNAL_SCENES.COLUMN_ETA', type: 'custom', customTemplate: this.etaCell(), width: '130px' },
    { field: 'statusKey', header: 'EXTERNAL_SCENES.COLUMN_STATUS', type: 'custom', customTemplate: this.statusCell(), width: '200px' },
    { field: 'id', header: 'EXTERNAL_SCENES.COLUMN_ACTION', type: 'custom', customTemplate: this.actionCell(), width: '170px' }
  ]);

  /** Row action of the Load column: the enabled "Load" button, or a disabled one carrying the row's state. */
  private toRowAction(row: ExternalSceneRowViewModel): TableAction {
    const state = resolveActionState(row);
    return {
      action: 'custom',
      label: this.translate.instant(state.labelKey),
      disabled: state.disabled,
      loading: state.loading
    };
  }

  /** Forwards a press of the row's enabled Load action; disabled and loading actions never reach this handler. */
  onRowAction(event: { row: ExternalSceneActionRow }): void {
    this.loadClick.emit(event.row.id);
  }

  onPreviewError(rowId: string): void {
    this.failedPreviewIds.update(current => new Set(current).add(rowId));
  }
}
